import React, { useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  backdropBlur?: boolean;
  stacked?: boolean;
}
const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl'
};
let modalCount = 0;
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  backdropBlur = false,
  stacked = false
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      modalCount++;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        modalCount--;
        if (modalCount === 0) {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
        }
      };
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const backdropZ = stacked ? 'z-[10000]' : 'z-[9990]';
  const modalZ = stacked ? 'z-[10010]' : 'z-[9999]';
  return createPortal(<Fragment data-id="element-252">
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black/40 ${backdropZ} transition-opacity ${backdropBlur ? 'backdrop-blur-[6px]' : ''}`} onClick={onClose} aria-hidden="true" data-id="element-253" />

      {/* Desktop Modal */}
      <div className={`fixed inset-0 ${modalZ} overflow-y-auto hidden lg:block`} data-id="element-254">
        <div className="flex min-h-full items-center justify-center p-4" data-id="element-255">
          <div className={`
              relative bg-white rounded-xl shadow-xl w-full
              transform transition-all
              ${sizeStyles[size]}
            `} role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title' : undefined} data-id="element-256">
            {title && <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-257">
                <h2 id="modal-title" className="text-lg font-semibold text-gray-900" data-id="element-258">
                  {title}
                </h2>
                <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-259">
                  <XIcon className="w-5 h-5" data-id="element-260" />
                </button>
              </div>}
            <div className={title ? '' : 'pt-4'} data-id="element-261">{children}</div>
          </div>
        </div>
      </div>

      {/* Mobile Modal (bottom sheet) */}
      <div className={`fixed inset-0 ${modalZ} lg:hidden flex items-end`} onClick={e => {
      if (e.target === e.currentTarget) onClose();
    }} data-id="element-262">
        <div className="relative bg-white rounded-t-2xl shadow-2xl w-full max-h-[92vh] flex flex-col animate-slide-up" role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title-mobile' : undefined} style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }} data-id="element-263">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0" data-id="element-264">
            <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-265" />
          </div>

          {title && <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0" data-id="element-266">
              <h2 id="modal-title-mobile" className="text-base font-bold text-gray-900" data-id="element-267">
                {title}
              </h2>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors -mr-1" aria-label="Cerrar" data-id="element-268">
                <XIcon className="w-5 h-5" data-id="element-269" />
              </button>
            </div>}
          <div className={`flex-1 overflow-y-auto ${title ? '' : 'pt-2'}`} data-id="element-270">
            {children}
          </div>
        </div>
      </div>
    </Fragment>, document.body);
}
export function ModalBody({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`px-5 lg:px-6 py-4 ${className}`} data-id="element-271">{children}</div>;
}
export function ModalFooter({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`px-5 lg:px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 ${className}`} data-id="element-272">
      {children}
    </div>;
}